"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "accent" | "outline" | "ghost" | "gradient";
  size?: "sm" | "md" | "lg";
  href?: string;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  magnetic?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className = "",
      variant = "primary",
      size = "md",
      href,
      isLoading,
      leftIcon,
      rightIcon,
      magnetic = false,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      "inline-flex items-center justify-center font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50 disabled:pointer-events-none cursor-pointer";
    
    const variantStyles = {
      primary: "bg-primary text-white hover:opacity-90 shadow-sm",
      secondary: "border border-primary text-primary bg-transparent hover:bg-primary/10",
      accent: "bg-accent text-white hover:opacity-90 shadow-sm",
      outline:
        "border border-border-muted text-foreground bg-transparent hover:bg-slate-100/50 dark:hover:bg-slate-800/50",
      ghost: "text-foreground bg-transparent hover:bg-slate-100/50 dark:hover:bg-slate-800/50",
      gradient:
        "bg-gradient-to-r from-primary to-accent text-white hover:opacity-90 shadow-sm",
    };

    const sizeStyles = {
      sm: "px-3.5 py-1.5 text-sm",
      md: "px-5 py-2.5 text-base",
      lg: "px-7 py-3.5 text-lg",
    };

    const combinedClassName = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`;

    // Handle magnetic hover pull state (if enabled)
    const [position, setPosition] = React.useState({ x: 0, y: 0 });
    
    const motionDivProps = magnetic
      ? {
          animate: { x: position.x, y: position.y },
          transition: { type: "spring" as const, stiffness: 150, damping: 15, mass: 0.1 },
          onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => {
            const { clientX, clientY } = e;
            const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
            const x = clientX - (left + width / 2);
            const y = clientY - (top + height / 2);
            setPosition({ x: x * 0.25, y: y * 0.25 });
          },
          onMouseLeave: () => setPosition({ x: 0, y: 0 }),
        }
      : {
          whileHover: { scale: 1.02 },
          whileTap: { scale: 0.98 },
        };

    const content = (
      <>
        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin text-current" />}
        {!isLoading && leftIcon && <span className="mr-2 inline-flex">{leftIcon}</span>}
        {children}
        {!isLoading && rightIcon && <span className="ml-2 inline-flex">{rightIcon}</span>}
      </>
    );

    if (href) {
      return (
        <motion.div {...motionDivProps} className="inline-block w-full sm:w-auto">
          <Link href={href} className={`${combinedClassName} w-full`}>
            {content}
          </Link>
        </motion.div>
      );
    }

    return (
      <motion.div {...motionDivProps} className="inline-block w-full sm:w-auto">
        <button
          ref={ref}
          disabled={disabled || isLoading}
          className={`${combinedClassName} w-full`}
          {...(props as any)}
        >
          {content}
        </button>
      </motion.div>
    );
  }
);

Button.displayName = "Button";
